import { useState, useEffect } from 'react';
import { watchlistAPI, autoSearchAPI, featureFlagsAPI, adminAPI } from '../lib/api';
import { useAuth } from '../lib/auth';
import { useNavigate } from 'react-router-dom';

import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Switch } from '../components/ui/switch';
import { Badge } from '../components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import {
  Search,
  Loader2,
  Plus,
  Trash2,
  Clock,
  PlayCircle,
  Flame,
  ExternalLink,
  Settings2,
  History,
  Globe,
  Users,
  Heart,
  MessageCircle,
  Share2,
  Calendar,
  CheckCircle2,
  AlertCircle,
  List,
  Zap,
  Bell,
  Shield,
  Crown,
  Database,
  FileText,
  Bot
} from 'lucide-react';
import { toast } from 'sonner';
import FeatureGuard from '../components/FeatureGuard';

export default function AutoSearch() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const isAdmin = user?.role === 'admin';

  const [schedule, setSchedule] = useState(null);
  const [results, setResults] = useState([]);
  const [activityLogs, setActivityLogs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [running, setRunning] = useState(false);
  const [featureFlag, setFeatureFlag] = useState(null);

  // Form State
  const [scheduleTime, setScheduleTime] = useState('12:00');
  const [scheduleEnabled, setScheduleEnabled] = useState(true);
  const [savingSchedule, setSavingSchedule] = useState(false);
  const [keywords, setKeywords] = useState('');
  const [gender, setGender] = useState('both');
  const [useAdminSource, setUseAdminSource] = useState(true);
  const [notifyChannels, setNotifyChannels] = useState({ email: true, telegram: false });

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      const [scheduleRes, resultsRes, logsRes, flagsRes] = await Promise.all([
        autoSearchAPI.getSchedule().catch(() => ({ data: null })),
        autoSearchAPI.getResults(),
        autoSearchAPI.getLogs().catch(() => ({ data: [] })),
        featureFlagsAPI.getAll()
      ]);
      
      setSchedule(scheduleRes.data);
      setResults(resultsRes.data || []);
      setActivityLogs(logsRes.data || []);
      
      const flag = flagsRes.data.find(f => f.id === 'auto_search');
      setFeatureFlag(flag);

      if (scheduleRes.data) {
        setScheduleTime(scheduleRes.data.time || '12:00');
        setScheduleEnabled(scheduleRes.data.enabled !== false);
        setKeywords(scheduleRes.data.keywords || '');
        setGender(scheduleRes.data.gender || 'both');
        setUseAdminSource(scheduleRes.data.use_admin_source !== undefined ? scheduleRes.data.use_admin_source : true);
        setNotifyChannels(scheduleRes.data.notify_channels || { email: true, telegram: false });
      }
    } catch (error) {
      console.error('Failed to fetch data:', error);
      const errorMsg = error.response?.data?.detail || error.message || 'Lỗi không xác định';
      toast.error(`Lỗi tải dữ liệu: ${errorMsg}`);
    } finally {
      setLoading(false);
    }
  };

  const handleSaveSchedule = async () => {
    setSavingSchedule(true);
    try {
      // Backend now handles Upsert (POST)
      await autoSearchAPI.createSchedule(scheduleTime, scheduleEnabled, {
        keywords,
        gender,
        use_admin_source: useAdminSource,
        notify_channels: notifyChannels
      });
      
      await fetchData();
      toast.success('Đã lưu cấu hình thành công!');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Lưu thất bại');
    } finally {
      setSavingSchedule(false);
    }
  };

  const handleRunNow = async () => {
    setRunning(true);
    try {
      const res = await autoSearchAPI.runNow({
        keywords,
        gender,
        use_admin_source: useAdminSource,
        notify_channels: notifyChannels
      });
      await fetchData(); 
      toast.success(res.data.message);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Chạy thất bại');
    } finally {
      setRunning(false);
    }
  };

  const handleDeleteResult = async (id) => {
    try {
      await autoSearchAPI.deleteResult(id);
      setResults(prev => prev.filter(r => r.id !== id));
      toast.success('Đã xóa kết quả');
    } catch (error) {
      toast.error('Không thể xóa kết quả');
    }
  };

  const handleClearHistory = async () => {
    if (!window.confirm('Bạn có chắc chắn muốn xóa toàn bộ lịch sử link đã tìm thấy?')) return;
    try {
      await autoSearchAPI.clearHistory();
      setResults([]);
      toast.success('Đã xóa toàn bộ lịch sử');
    } catch (error) {
      toast.error('Lỗi khi xóa lịch sử');
    }
  };

  const getEngagementBadge = (level) => {
    const config = {
      high: { label: 'Cao', className: 'bg-[#EBF5ED] text-[#1E7132]' },
      medium: { label: 'TB', className: 'bg-[#FEF9E6] text-[#8F6702]' },
      low: { label: 'Thấp', className: 'bg-[#FCE9EC] text-[#A8112A]' },
    };
    const { label, className } = config[level] || config.low;
    return <span className={`px-2.5 py-1 rounded-full text-xs font-semibold ${className}`}>{label}</span>;
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-[400px]">
        <Loader2 size={32} className="animate-spin text-[#1877F2]" />
      </div>
    );
  }

  return (
    <FeatureGuard feature={featureFlag}>
      <div className="space-y-6" data-testid="auto-search-page">
        {/* Header */}
        <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3 animate-fadeIn">
          <div>
            <h1 className="text-2xl font-bold text-[#1C1E21] flex items-center gap-2">
              <Zap className="text-[#F5C33B]" />
              Tự động tìm Hotface
              {featureFlag?.is_vip && (
                <span className="ml-2 px-2 py-0.5 bg-amber-100 text-amber-700 text-[10px] uppercase font-bold rounded-full border border-amber-200 flex items-center gap-1">
                  <Crown size={12} />
                  VIP
                </span>
              )}
            </h1>
            <p className="text-[#606770] flex items-center gap-2">
              Đặt lịch tự động tìm các trang/nhóm có tương tác cao {featureFlag?.is_vip ? '' : (featureFlag?.credit_cost > 0 ? `(${featureFlag.credit_cost} cr/lần)` : '(Miễn phí)')}
            </p>
          </div>
          {isAdmin && (
            <Button
              onClick={handleRunNow}
              disabled={running || (!isAdmin && featureFlag?.is_vip && user?.subscription_type !== 'monthly')}
              className="bg-[#31A24C] hover:bg-[#2B8A3E] w-full sm:w-auto"
            >
              {running ? (
                <><Loader2 size={16} className="mr-2 animate-spin" />Đang tìm...</>
              ) : (
                <><PlayCircle size={16} className="mr-2" />Chạy ngay (Admin)</>
              )}
            </Button>
          )}
        </div>

        <Tabs defaultValue="results" className="animate-fadeIn stagger-1">
          <TabsList className="bg-[#F0F2F5] p-1 flex-wrap h-auto justify-start gap-1">
            <TabsTrigger value="results" className="data-[state=active]:bg-white text-xs sm:text-sm">
              <Zap size={16} className="mr-1 sm:mr-2 text-amber-500" />
              Kết quả (Hôm nay)
            </TabsTrigger>
            <TabsTrigger value="full-history" className="data-[state=active]:bg-white text-xs sm:text-sm">
              <Database size={16} className="mr-1 sm:mr-2 text-[#1877F2]" />
              Lịch sử Link
            </TabsTrigger>
            <TabsTrigger value="activity-logs" className="data-[state=active]:bg-white text-xs sm:text-sm">
              <FileText size={16} className="mr-1 sm:mr-2 text-green-600" />
              Nhật ký hoạt động
            </TabsTrigger>
            <TabsTrigger value="config" className="data-[state=active]:bg-white text-xs sm:text-sm">
              <Settings2 size={16} className="mr-1 sm:mr-2 text-[#606770]" />
              Cấu hình
            </TabsTrigger>
          </TabsList>

          {/* Today's Results Tab */}
          <TabsContent value="results" className="mt-6">
            <Card className="border-none shadow-sm">
              <CardHeader>
                <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
                  <div>
                    <CardTitle className="text-lg font-bold text-[#1C1E21]">Tìm thấy hôm nay</CardTitle>
                    <CardDescription>Các liên kết được phát hiện trong 24h qua</CardDescription>
                  </div>
                  <div className="bg-amber-50 border border-amber-100 p-2 rounded-lg text-[10px] text-amber-800 leading-tight max-w-sm">
                    <div className="font-extrabold flex items-center gap-1 mb-0.5">
                      <Shield size={10} /> THÔNG BÁO BẢO MẬT
                    </div>
                    Vì an toàn thông tin, dữ liệu sẽ được làm mới sau <strong>7-10 ngày</strong>. Bạn nên lưu các Hotface chất lượng vào ghi chú cá nhân.
                  </div>
                </div>
              </CardHeader>
              <CardContent>
                {(() => {
                  const now = new Date();
                  const todayLocal = new Date(now.getFullYear(), now.getMonth(), now.getDate());
                  const isToday = (r) => {
                    if (!r.found_at) return false;
                    const foundDate = new Date(r.found_at);
                    return foundDate >= todayLocal;
                  };
                  const todayResults = results.filter(isToday);
                  return todayResults.length > 0 ? (
                  <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                    {todayResults.slice().reverse().map((result) => (
                      <Card key={result.id} className="overflow-hidden border border-[#E4E6EB] hover:shadow-md transition-shadow bg-white rounded-2xl">
                        <div className="p-4 space-y-3">
                          <div className="flex justify-between items-start">
                            <div className="flex items-center gap-3">
                              <div className="w-10 h-10 rounded-full bg-[#1877F2]/10 flex items-center justify-center text-[#1877F2] font-bold">
                                {result.page_name?.charAt(0) || 'F'}
                              </div>
                              <div className="min-w-0">
                                <h4 className="font-bold text-[#1C1E21] line-clamp-1">{result.page_name}</h4>
                                <p className="text-[10px] text-[#606770]">{result.found_at ? new Date(result.found_at).toLocaleTimeString('vi-VN') : ''}</p>
                              </div>
                            </div>
                            <div className="flex flex-col items-end gap-2">
                               {getEngagementBadge(result.engagement_level)}
                            </div>
                          </div>
                          
                          <div className="flex items-center gap-4 text-xs text-[#606770]">
                             <span className="flex items-center gap-1"><Heart size={12} className="text-[#E41E3F]" /> {(result.likes || 0).toLocaleString()}</span>
                             <span className="flex items-center gap-1"><MessageCircle size={12} className="text-[#1877F2]" /> {(result.comments || 0).toLocaleString()}</span>
                             {result.reels_views > 0 && (
                               <span className="flex items-center gap-1 font-bold text-purple-600"><Bot size={12} /> {(result.reels_views || 0).toLocaleString()}</span>
                             )}
                          </div>

                          <div className="pt-2 flex gap-2">
                             <Button 
                               variant="outline" 
                               size="sm" 
                               className="flex-1 text-[11px] h-8 rounded-lg"
                               onClick={() => window.open(result.url, '_blank')}
                             >
                               Xem trang
                             </Button>
                             <Button 
                               size="sm" 
                               className="flex-1 text-[11px] h-8 rounded-lg bg-[#1877F2] hover:bg-[#166FE5]"
                               onClick={() => navigate(`/analyze?url=${encodeURIComponent(result.url)}&auto=true`)}
                             >
                               Phân tích
                             </Button>
                             {isAdmin && (
                               <Button
                                 size="sm"
                                 variant="outline"
                                 className="text-[11px] h-8 rounded-lg border-amber-200 text-amber-700 hover:bg-amber-50"
                                 onClick={async () => {
                                   try {
                                     await adminAPI.addFromAutoResult(result.url, gender, `From AutoSearch: ${keywords}`);
                                     toast.success('Đã thêm vào nguồn chung!');
                                   } catch (err) {
                                     toast.error(err.response?.data?.detail || 'Thêm thất bại');
                                   }
                                 }}
                               >
                                 <Database size={12} className="mr-1" />
                                 + Nguồn
                               </Button>
                             )}
                          </div>
                        </div>
                      </Card>
                    ))}
                  </div>
                ) : (
                  <div className="text-center py-20 bg-[#F7F8FA] rounded-2xl">
                    <div className="w-16 h-16 bg-[#E4E6EB] rounded-full flex items-center justify-center mx-auto mb-4">
                      <Zap className="text-[#606770]" size={32} />
                    </div>
                    <h3 className="text-lg font-semibold text-[#1C1E21]">Hôm nay chưa có link mới</h3>
                    <p className="text-[#606770] max-w-xs mx-auto">Hệ thống sẽ cập nhật tại đây ngay khi tìm thấy trang tương tác cao.</p>
                  </div>
                );
                })()}
              </CardContent>
            </Card>
          </TabsContent>

          {/* Full History Tab */}
          <TabsContent value="full-history" className="mt-6">
            <Card className="border-none shadow-sm">
              <CardHeader className="flex flex-row items-center justify-between">
                 <div>
                    <CardTitle className="text-lg font-bold">Lịch sử toàn bộ link</CardTitle>
                    <CardDescription>Kho lưu trữ tất cả các Hotface đã từng phát hiện</CardDescription>
                 </div>
              </CardHeader>
              <CardContent>
                 <div className="space-y-3">
                    {results.slice().reverse().map((result) => (
                       <div key={result.id} className="flex flex-col sm:flex-row sm:items-center justify-between p-4 border rounded-2xl bg-white hover:bg-[#F7F8FA] transition-colors gap-3">
                          <div className="flex items-center gap-4">
                             <div className="w-10 h-10 rounded-full bg-gray-100 flex items-center justify-center text-gray-400 font-bold shrink-0">
                                {result.page_name?.charAt(0) || 'F'}
                             </div>
                             <div className="min-w-0">
                                <h4 className="font-bold text-[#1C1E21] truncate">{result.page_name}</h4>
                                <p className="text-[11px] text-[#8C939D] flex items-center gap-1">
                                   <Database size={10} /> Đã lưu: {new Date(result.found_at).toLocaleDateString('vi-VN')}
                                </p>
                             </div>
                          </div>
                          <div className="flex items-center gap-3">
                             <Button size="sm" variant="ghost" className="text-[#1877F2] text-xs" onClick={() => window.open(result.url, '_blank')}>
                                <ExternalLink size={14} className="mr-1" /> Mở
                             </Button>
                          </div>
                       </div>
                    ))}
                    {results.length === 0 && <p className="text-center py-10 text-gray-400 italic">Chưa có dữ liệu lịch sử</p>}
                 </div>
              </CardContent>
            </Card>
          </TabsContent>

          {/* Activity Logs Tab */}
          <TabsContent value="activity-logs" className="mt-6">
            <Card className="border-none shadow-sm">
              <CardHeader>
                <CardTitle className="text-lg font-bold">Nhật ký hoạt động</CardTitle>
                <CardDescription>Chi tiết các phiên chạy của bot tìm kiếm tự động</CardDescription>
              </CardHeader>
              <CardContent>
                <div className="space-y-4">
                  {activityLogs.length > 0 ? activityLogs.map((log) => (
                    <div key={log.id} className="flex items-center justify-between p-4 border rounded-2xl bg-white hover:bg-gray-50 transition-colors">
                      <div className="flex items-center gap-4">
                         <div className={`w-10 h-10 rounded-full flex items-center justify-center ${log.status === 'success' ? 'bg-green-100 text-green-600' : 'bg-red-100 text-red-600'}`}>
                            {log.status === 'success' ? <CheckCircle2 size={24} /> : <AlertCircle size={24} />}
                         </div>
                         <div>
                            <p className="font-bold text-[#1C1E21] capitalize flex items-center gap-2">
                               Bộ quét: {log.status === 'success' ? 'Thành công' : 'Thất bại'}
                               {log.is_auto && <Badge variant="secondary" className="text-[9px] bg-blue-50 text-blue-600 border-none">Auto</Badge>}
                            </p>
                            <p className="text-xs text-[#606770]">{new Date(log.run_at).toLocaleString('vi-VN')}</p>
                         </div>
                      </div>
                      <div className="text-right">
                         <p className={`text-sm font-bold ${log.results_count > 0 ? 'text-[#1877F2]' : 'text-gray-400'}`}>
                            + {log.results_count} Hotface
                         </p>
                         <p className="text-[10px] text-[#8C939D] italic">{log.message || log.keywords}</p>
                      </div>
                    </div>
                  )) : (
                    <div className="text-center py-20 grayscale opacity-40">
                       <FileText size={48} className="mx-auto mb-2" />
                       <p>Chưa ghi nhận phiên chạy nào</p>
                    </div>
                  )}
                </div>
              </CardContent>
            </Card>
          </TabsContent>

          {/* Config Tab */}
          <TabsContent value="config" className="mt-6 space-y-6">
            <Card className="border-none shadow-sm">
              <CardHeader>
                <CardTitle className="text-lg font-bold flex items-center gap-2">
                  <Settings2 className="text-[#1877F2]" />
                  Cấu hình tìm kiếm & Lịch hẹn
                </CardTitle>
                <CardDescription>Thiết lập cách thức và thời gian bot hoạt động</CardDescription>
              </CardHeader>
              <CardContent className="space-y-8">
                {/* Keywords & Gender */}
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  <div className="space-y-3">
                    <Label className="font-bold flex items-center gap-2"><Search size={16} /> Từ khóa tìm kiếm</Label>
                    <Input 
                      placeholder="VD: mỹ phẩm, kem trắng da..." 
                      value={keywords} 
                      onChange={(e) => setKeywords(e.target.value)}
                      className="rounded-xl border-[#E4E6EB]"
                    />
                    <p className="text-[11px] text-[#606770] italic">* Các từ khóa cách nhau bằng dấu phẩy</p>
                  </div>
                  <div className="space-y-3">
                    <Label className="font-bold flex items-center gap-2"><Users size={16} /> Giới tính mục tiêu</Label>
                    <Select value={gender} onValueChange={setGender}>
                      <SelectTrigger className="rounded-xl border-[#E4E6EB]">
                        <SelectValue placeholder="Chọn giới tính" />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="both">Tất cả (Nam/Nữ)</SelectItem>
                        <SelectItem value="male">Nam</SelectItem>
                        <SelectItem value="female">Nữ</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                </div>

                {/* Automation Time */}
                <div className="p-5 border rounded-2xl bg-[#F7F8FA] space-y-4">
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-3">
                      <Clock size={20} className="text-[#1877F2]" />
                      <div>
                        <Label className="font-bold text-base">Giờ chạy tự động</Label>
                        <p className="text-sm text-[#606770]">Bot sẽ tự khởi động vào khung giờ này</p>
                      </div>
                    </div>
                    <Switch checked={scheduleEnabled} onCheckedChange={setScheduleEnabled} />
                  </div>
                  <div className="flex items-center gap-4">
                    <Input 
                      type="time" 
                      value={scheduleTime} 
                      onChange={(e) => setScheduleTime(e.target.value)}
                      className="w-40 text-lg font-bold h-12 rounded-xl text-center"
                    />
                    <div className="text-sm text-[#606770] flex items-center gap-2 italic">
                       <Shield size={14} /> Hệ thống sẽ chạy quét 1 lần duy nhất mỗi ngày
                    </div>
                  </div>
                </div>

                {/* Notifications & Admin Pool */}
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  <div className="space-y-4">
                    <Label className="font-bold flex items-center gap-2"><Bell size={16} /> Nhận thông báo qua</Label>
                    <div className="flex gap-6 p-4 border rounded-xl">
                      <div className="flex items-center gap-2">
                        <Switch 
                          checked={notifyChannels.email} 
                          onCheckedChange={(c) => setNotifyChannels(prev => ({...prev, email: c}))} 
                        />
                        <Label>Email</Label>
                      </div>
                      <div className="flex items-center gap-2">
                        <Switch 
                          checked={notifyChannels.telegram} 
                          onCheckedChange={(c) => setNotifyChannels(prev => ({...prev, telegram: c}))} 
                        />
                        <Label>Telegram</Label>
                      </div>
                    </div>
                  </div>

                  {isAdmin && (
                    <div className="space-y-4">
                      <Label className="font-bold flex items-center gap-2 text-amber-700">
                        <Zap size={16} /> Chế độ đặc biệt (Admin)
                      </Label>
                      <div className="p-4 border border-amber-200 bg-amber-50 rounded-xl flex items-center justify-between">
                        <div>
                          <Label className="font-bold text-amber-900">Bù đắp từ "Kho Ma Thuật"</Label>
                          <p className="text-xs text-amber-700">Tự động lấy link dự phòng nếu cần</p>
                        </div>
                        <Switch checked={useAdminSource} onCheckedChange={setUseAdminSource} className="data-[state=checked]:bg-amber-600" />
                      </div>
                    </div>
                  )}
                </div>

                <div className="pt-4 border-t">
                  <Button 
                    onClick={handleSaveSchedule} 
                    className="w-full bg-[#1877F2] hover:bg-[#166FE5] h-12 text-base font-bold rounded-xl shadow-lg shadow-blue-100"
                    disabled={savingSchedule}
                  >
                    {savingSchedule ? <><Loader2 className="mr-2 animate-spin" /> Đang lưu...</> : <><CheckCircle2 className="mr-2" /> Lưu tất cả cấu hình</>}
                  </Button>
                </div>
              </CardContent>
            </Card>
          </TabsContent>
        </Tabs>
      </div>
    </FeatureGuard>
  );
}
